<template>
  <div v-if="product" class="w-full space-y-8 lg:space-y-12 xl:space-y-16">
    <!-- Hero Detail -->
    <SectionsHeroDetail
      :key="'heroDetail'"
      :title="heroTitle"
      :description="heroDescription"
      :media="heroMedia"
      :backgroundColor="heroBackgroundColor"
      :color="heroColor"
      :breadcrumbs="breadcrumbs"
    />
    <!-- Hero Detail -->

    <!-- Content -->
    <section class="w-full bg-[#F8F9FA] py-8 lg:py-12 xl:py-16 !m-0">
      <div class="container">
        <div :class="productGridClass">
          <div class="w-full h-full">
            <PluginsFancybox
              class="h-full"
              :options="fancyboxOptions"
            >
              <client-only class="h-full">
                <div :class="mediaContainerClass">
                  <!-- Multi Image Swiper -->
                  <Swiper
                    v-if="hasMultipleMedia"
                    class="w-full overflow-hidden rounded-xl"
                    :modules="mainSwiperModules"
                    :thumbs="{ swiper: thumbsSwiper }"
                    :navigation="false"
                    :spaceBetween="16"
                  >
                    <SwiperSlide
                      v-for="(item, index) in productOverview?.media"
                      :key="item?.id || index"
                    >
                      <a
                        class="block w-full overflow-hidden"
                        :class="mainImageLinkClass"
                        :href="`${baseUrl}${item?.formats?.xlarge_webp?.url ?? item?.formats?.large_webp?.url ?? item?.formats?.medium_webp?.url ?? item?.url}`"
                        data-fancybox="product-gallery"
                      >
                        <AtomsImage
                          class="w-full"
                          :class="productImageClass"
                          :media="item"
                          :alt="item?.alternativeText ?? item?.hash ?? item?.name"
                          sizeHint="third"
                        />
                      </a>
                    </SwiperSlide>
                  </Swiper>

                  <!-- Single Image -->
                  <div
                    v-else-if="hasSingleMedia"
                    class="w-full h-full overflow-hidden lg:flex-1 lg:order-2"
                  >
                    <a
                      class="block w-full h-full"
                      :class="mainImageLinkClass"
                      :href="singleMediaHref"
                      data-fancybox="product-gallery"
                    >
                      <AtomsImage
                        class="w-full h-full "
                        :class="productImageClass"
                        :media="singleMedia"
                        :alt="singleMedia?.alternativeText ?? singleMedia?.hash ?? singleMedia?.name"
                        sizeHint="third"
                      />
                    </a>
                  </div>

                  <!-- Color Swatch -->
                  <div
                    v-else-if="shouldShowColorSwatch"
                    class="w-full h-full"
                    :class="productColorSwatchClass"
                    :style="{ backgroundColor: productOverviewColor }"
                  ></div>

                  <Swiper
                    v-if="hasMultipleMedia"
                    class="w-full swiper-product-thumb !px-4 !pb-4 lg:!px-8 lg:!pb-8"
                    :modules="thumbsSwiperModules"
                    :spaceBetween="12"
                    :slidesPerView="thumbSlidesPerView"
                    :freeMode="true"
                    :watchSlidesProgress="true"
                    :direction="thumbsDirection"
                    @swiper="setThumbsSwiper"
                  >
                    <SwiperSlide
                      v-for="(item, index) in productOverview?.media"
                      :key="`thumb-${index}`"
                    >
                      <button
                        type="button"
                        :aria-label="item?.alternativeText ?? item?.name ?? item?.hash ?? `Thumbnail ${index + 1}`"
                        :class="thumbButtonClass"
                      >
                        <AtomsImage
                          class="w-full block"
                          :class="productThumbImageClass"
                          :media="item"
                          :alt="item?.alternativeText ?? item?.hash ?? item?.name"
                          sizeHint="icon"
                        />
                      </button>
                    </SwiperSlide>
                  </Swiper>
                </div>
              </client-only>
            </PluginsFancybox>
          </div>
          <div class="w-full flex flex-col gap-1 p-5 lg:px-8 lg:pt-8 lg:pb-4 xl:px-10 xl:pt-10 xl:pb-6">
            <!-- title -->
            <h1 class="text-[18px] lg:text-[28px] font-black uppercase text-gray-900 tracking-wide font-saira m-0">
              {{ productTitle }}
            </h1>
            <!-- title -->

            <div class="flex flex-wrap items-center gap-3 text-[13px] mt-2">
              <div v-if="productSku" class="flex items-center gap-2">
                <span class="text-gray-400 font-medium">{{ t("Product Code") }}:</span>
                <span class="font-bold tracking-wider text-gray-600">{{
                  productSku
                }}</span>
              </div>
              <div v-if="shouldShowColorSwatch" class="flex items-center">
                <span class="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-gray-50 border border-gray-200/80">
                  <span
                    class="w-5 h-5 rounded-full ring-2 ring-white shadow-[0_1px_3px_rgba(0,0,0,0.18)]"
                    :style="{ backgroundColor: productOverviewColor }"
                  ></span>
                  <span class="font-semibold tracking-wider text-gray-600 text-[12px] uppercase">{{
                    productOverviewColor
                  }}</span>
                </span>
              </div>
              <div
                v-if="stockBadge"
                class="stock-badge"
                :class="stockBadge.tone"
              >
                {{ stockBadge.label }}
              </div>
            </div>

            <!-- Button -->
            <div class="w-full mt-4 flex gap-3" v-if="productPurchaseLink">
              <AtomsButton
                :label="t('Products.GetQuote')"
                :link="productPurchaseLink"
                target="blank"
                color="first"
                size="medium"
                class="!w-1/2 !px-1.5 sm:!px-3 lg:!px-6 xl:!px-10 !rounded-lg lg:!rounded-xl !font-bold !text-[10.5px] lg:!text-[13px] !tracking-normal lg:!tracking-wider !whitespace-nowrap"
              />
            </div>
            <!-- Button -->

            <!-- Price -->
            <div
              v-if="formattedPrice || formattedOldPrice"
              class="w-full mt-4 flex items-center gap-2 font-saira"
            >
              <span
                v-if="formattedPrice"
                class="font-extrabold text-2xl lg:text-3xl pr-2 text-brand-second"
                >{{ formattedPrice }}</span
              >
              <span
                v-if="formattedOldPrice"
                class="font-normal text-xl lg:text-2xl line-through opacity-40"
                >{{ formattedOldPrice }}</span
              >
            </div>
            <!-- Price -->

            <!-- Description -->
            <div
              class="w-full rich-text mt-5 font-light text-gray-600 text-[14px] leading-relaxed"
              v-if="productDescription"
              v-html="productDescription"
            ></div>
            <!-- Description -->

            <!-- Overview Features -->
            <div
              v-if="productOverviewFeatureIcons.length"
              class="w-full mt-6 grid grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-4 lg:gap-3 xl:gap-4 border-0 border-t border-solid border-gray-100 pt-6"
            >
              <div
                v-for="(feature, index) in productOverviewFeatureIcons"
                :key="feature?.id ?? index"
                class="flex flex-col items-center text-center gap-2"
              >
                <AtomsImage
                  v-if="feature?.icon"
                  :media="feature.icon"
                  :alt="feature?.title || 'Feature icon'"
                  sizeHint="icon"
                  class="h-12 opacity-80"
                />
                <div v-if="feature?.title" class="text-[11px] leading-[22px] font-semibold text-gray-500 px-2 uppercase tracking-wide">
                  {{ feature.title }}
                </div>
              </div>
            </div>
            <!-- Overview Features -->

            <!-- Other Products -->
            <div
              v-if="relatedProductsMini.length && product?.id"
              class="w-full border-0 border-t border-solid border-gray-100 mt-5 py-6 overflow-hidden"
            >
              <div class="text-[16px] lg:text-[18px] font-bold font-saira text-gray-900 mb-4">
                {{ t("Other Products") }}
              </div>
            
              <client-only>
                <Swiper
                  :key="relatedProductsEndpoint"
                  class="w-full swiper-related-products"
                  :modules="[FreeMode]"
                  :spaceBetween="12"
                  :slidesPerView="3.7"
                  :breakpoints="relatedProductsBreakpoints"
                  :freeMode="true"
                  :grabCursor="true"
                  :preventClicks="true"
                  :preventClicksPropagation="false"
                  :touchStartPreventDefault="false"
                  :threshold="5"
                >
                  <SwiperSlide
                    v-for="(item, index) in relatedProductsMini"
                    :key="item?.documentId || index"
                  >
                    <NuxtLink
                      :to="buildProductLink(item)"
                      class="block group bg-white rounded-xl border border-solid border-gray-200 shadow-sm hover:-translate-y-1 transition-all duration-300"
                    >
                      <AtomsImage
                        v-if="item?.productOverview?.media?.length"
                        :media="item.productOverview.media[0]"
                        :alt="item?.productOverview?.media?.[0]?.alternativeText || item?.title || 'Product'"
                        sizeHint="icon"
                        class="aspect-square object-contain w-full rounded-t-xl pointer-events-none"
                      />
                      <div
                        v-else-if="item?.productOverview?.color"
                        class="aspect-square w-full rounded-t-xl"
                        :style="{ backgroundColor: item.productOverview.color }"
                      ></div>
                      <div
                        v-if="item?.title"
                        class="py-2.5 px-1.5 min-h-[2.75rem] flex items-center justify-center text-[10px] lg:text-[11px] text-center font-bold uppercase tracking-wider text-gray-600 group-hover:text-brand-first transition-colors duration-300 leading-tight"
                      >
                        {{ item.title }}
                      </div>
                    </NuxtLink>
                  </SwiperSlide>
                </Swiper>
              </client-only>
            </div>
            <!-- Other Products -->
          </div>
        </div>
      </div>
    </section>
    <!-- Content -->

    <SectionsSectionRenderer :sections="productSections" />
    <SectionsSectionRenderer :sections="productTabSections" />
  </div>
</template>
<script setup>
import { computed, ref, watch } from "vue";
import qs from "qs";
import { useApi } from "~/server/getApi";
import { useI18n } from "vue-i18n";
import { useLocalePath } from "#i18n";
// import { Swiper, SwiperSlide } from "swiper/vue"; // Registered globally via plugins/swiper.client.js
import { FreeMode, Navigation, Thumbs } from "swiper/modules";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import { useIsMobile } from "~/composables/utils";
import { useMetaTags } from "~/composables/headConfig";
import { useSiteSettingsStore } from "@/store/index";
import {
  buildJsonLdScripts,
  createBreadcrumbSchema,
  createFaqSchema,
  createWebPageSchema,
  stripHtmlTags,
} from "~/composables/schemaOrg";

const props = defineProps({
  forcedSlug: {
    type: String,
    default: null,
  },
});

const { t, locale } = useI18n();
const { isMobile } = useIsMobile();
const localePath = useLocalePath();
const route = useRoute();
const siteSettingsStore = useSiteSettingsStore();
const baseProductsPath = computed(() => {
  const localized = localePath({ name: "products" });
  return localized || "/products";
});
const defaultCategoryQueryKey = "category";
const categoryQueryKey = computed(() => {
  const translated = t("Routes.CategoryQuery");
  if (typeof translated === "string" && translated.length) {
    return translated !== "Routes.CategoryQuery"
      ? translated
      : defaultCategoryQueryKey;
  }
  return defaultCategoryQueryKey;
});

const slugParam = computed(() => props.forcedSlug || route.params.slug);

const buildCategoryLinkPath = (slug) => {
  if (!slug) {
    return baseProductsPath.value;
  }
  const prefix = locale.value === "tr" ? "" : `/${locale.value}`;
  return `${prefix}/${encodeURIComponent(slug)}`;
};

const runtimeConfig = useRuntimeConfig();
const baseUrl = runtimeConfig.public.baseUrl;
const siteUrl = runtimeConfig.public.siteUrl;
const fullUrl = computed(() => `${siteUrl}${route.fullPath}`);

const productQuery = computed(() =>
  qs.stringify(
    {
      filters: {
        slug: {
          $eq: slugParam.value,
        },
      },
      populate: {
        category: {
          populate: "*",
        },
        productOverview: {
          populate: "*",
        },
      },
    },
    { encodeValuesOnly: true },
  ),
);

const productEndpoint = computed(
  () => `product-lists?${productQuery.value}&pLevel`,
);
const {
  data: productData,
  fetchData: fetchProductData,
  triggerError,
} = useApi(productEndpoint);

const product = computed(() => {
  const items = productData.value;
  if (Array.isArray(items)) {
    return items[0] || null;
  }
  return items || null;
});

const categoryFields = ["title", "slug"];
const buildSubcategoryPopulateConfig = (depth = 3) => {
  const config = { fields: categoryFields };
  if (depth > 0)
    config.populate = { subcategories: buildSubcategoryPopulateConfig(depth - 1) };
  return config;
};

const productCategoriesQuery = computed(() =>
  qs.stringify(
    {
      filters: { parent: { id: { $null: true } } },
      fields: categoryFields,
      populate: { subcategories: buildSubcategoryPopulateConfig() },
    },
    { encodeValuesOnly: true },
  ),
);

const { data: productCategoriesData, fetchData: fetchProductCategoriesData } =
  useApi(computed(() => `product-categories?${productCategoriesQuery.value}`), true);

// useAsyncData auto-fetches — no need to call fetchData() again

const findCategoryPath = (categories = [], slug, path = []) => {
  for (const category of categories) {
    const currentPath = [...path, category];
    if (category?.slug === slug) return currentPath;
    const childPath = findCategoryPath(category?.subcategories || [], slug, currentPath);
    if (childPath.length) return childPath;
  }
  return [];
};


const productSections = computed(() => product.value?.sections || []);
const productCategory = computed(() => product.value?.category || null);
const productCategorySlug = computed(() => {
  const category = productCategory.value;
  if (category?.slug) {
    return category.slug;
  }
  return category?.data?.attributes?.slug || null;
});
const productCategoryTitle = computed(() => {
  const category = productCategory.value;
  if (category?.title) {
    return category.title;
  }
  return category?.data?.attributes?.title || null;
});

const fullCategoryPath = computed(() => {
  const categories = productCategoriesData?.value || [];
  if (!productCategorySlug.value) return [];
  return findCategoryPath(categories, productCategorySlug.value);
});
const productCategoryLink = computed(() => {
  if (!productCategorySlug.value) {
    return null;
  }
  return buildCategoryLinkPath(productCategorySlug.value);
});
const productOverview = computed(() => product.value?.productOverview || {});
const productTitle = computed(() => product.value?.title || "");
const productDescription = computed(() => productOverview.value?.description);
const normalizeRelationItems = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.data)) {
    return value.data.map((item) =>
      item?.attributes ? { id: item.id, ...item.attributes } : item,
    );
  }
  if (value?.data) {
    const item = value.data;
    return [item?.attributes ? { id: item.id, ...item.attributes } : item];
  }
  return [];
};
const productTabSections = computed(() => {
  const technicalTabs = normalizeRelationItems(product.value?.productTechnical);
  const featureTabs = normalizeRelationItems(product.value?.productFeatures);
  return [...technicalTabs, ...featureTabs]
    .map((section) => ({
      ...section,
      __component: section?.__component || "sections.tab",
      enabled: section?.enabled ?? true,
    }))
    .filter((section) => section?.enabled && Array.isArray(section?.items));
});
const productOverviewFeatureIcons = computed(() => {
  const iconFeatures = normalizeRelationItems(
    productOverview.value?.featuresIcon,
  );
  const legacyFeatures = normalizeRelationItems(
    productOverview.value?.features,
  );
  return [...iconFeatures, ...legacyFeatures]
    .filter((feature) => feature?.enabled ?? true)
    .slice(0, 6);
});
const productOverviewColor = computed(() => productOverview.value?.color || "");
const mediaItems = computed(() => productOverview.value?.media || []);
const hasSingleMedia = computed(() => mediaItems.value.length === 1);
const hasMultipleMedia = computed(() => mediaItems.value.length > 1);
const singleMedia = computed(() => mediaItems.value[0] || null);
const singleMediaHref = computed(() => {
  const item = singleMedia.value;
  if (!item) return "";
  const url =
    item?.formats?.xlarge_webp?.url ??
    item?.formats?.large_webp?.url ??
    item?.formats?.medium_webp?.url ??
    item?.url ??
    "";
  if (!url) return "";
  return url.startsWith("http") ? url : `${baseUrl}${url}`;
});
const heroDetail = computed(() => product.value?.heroDetail || null);
const heroTitle = computed(
  () => heroDetail.value?.title || productTitle.value || "",
);
const heroDescription = computed(
  () => heroDetail.value?.description || product.value?.description || "",
);
const defaultHeroMedia = { url: '/img/hero-detail.webp', alternativeText: 'Hero Detail' };
const heroMedia = computed(
  () => heroDetail.value?.media || product.value?.listMedia || defaultHeroMedia,
);
const heroBackgroundColor = computed(
  () => heroDetail.value?.backgroundColor || product.value?.backgroundColor,
);
const heroColor = computed(
  () => heroDetail.value?.color || product.value?.color,
);

const formatCurrency = (value, currency) => {
  if (value === undefined || value === null) {
    return null;
  }
  try {
    return new Intl.NumberFormat(locale.value === "tr" ? "tr-TR" : "en-US", {
      style: "currency",
      currency: currency || "TRY",
    }).format(value);
  } catch (_err) {
    return `${value} ${currency || ""}`.trim();
  }
};

const formattedPrice = computed(() =>
  formatCurrency(productOverview.value?.price, productOverview.value?.currency),
);
const formattedOldPrice = computed(() =>
  formatCurrency(
    productOverview.value?.oldPrice,
    productOverview.value?.currency,
  ),
);

const productSku = computed(
  () =>
    product.value?.sku ||
    productOverview.value?.sku ||
    product.value?.code ||
    null,
);

const productPurchaseLabel = computed(() => t("Buy Now"));
const whatsappPhoneNumber = computed(() => {
  const raw = siteSettingsStore.fixedMenu?.whatsappPhone || siteSettingsStore.footerContact?.whatsapp || siteSettingsStore.phone || "";
  let num = String(raw).replace(/\D+/g, "");
  if (num.startsWith("0")) num = "9" + num;
  if (!num.startsWith("90") && num.length === 10) num = "90" + num;
  return num;
});
const productPurchaseLink = computed(() => {
  const message = productTitle.value
    ? t("Product Buy Message", { title: productTitle.value })
    : t("Product Buy Message Generic");
  const textWithUrl = `${message} ${fullUrl.value}`.trim();
  return `https://api.whatsapp.com/send?phone=${whatsappPhoneNumber.value}&text=${encodeURIComponent(
    textWithUrl,
  )}`;
});

const stockBadge = computed(() => {
  const item = product.value;
  if (!item) return null;
  const inStock = item.stockStatus === "in_stock" || item.available;
  
  if (!inStock) return null;
  
  return {
    label: t("Products.InStock"),
    tone: "stock-badge--success",
  };
});

const sampleCtaLink = computed(() => {
  const message = productTitle.value
    ? t("Product Sample Message", { title: productTitle.value })
    : t("Product Sample Message Generic");
  const textWithUrl = `${message} ${fullUrl.value}`.trim();
  return `https://api.whatsapp.com/send?phone=${whatsappPhoneNumber.value}&text=${encodeURIComponent(
    textWithUrl,
  )}`;
});

const breadcrumbs = computed(() => {
  const crumbs = [
    {
      enabled: true,
      link: locale.value === "tr" ? "/" : `/${locale.value}`,
      title: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 md:w-5 md:h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>',
    },
    {
      enabled: true,
      link: baseProductsPath.value,
      title: t("Breadcrumb.Products"),
    },
  ];

  fullCategoryPath.value.forEach((category) => {
    if (category?.title && category?.slug) {
      crumbs.push({
        enabled: true,
        title: category.title,
        link: buildCategoryLinkPath(category.slug),
      });
    }
  });

  if (productTitle.value) {
    crumbs.push({
      enabled: true,
      title: productTitle.value,
    });
  }

  return crumbs;
});

const thumbsSwiper = ref(null);
const setThumbsSwiper = (swiper) => {
  thumbsSwiper.value = swiper;
};

const relatedProductsQuery = computed(() => {
  if (!productCategorySlug.value) {
    return null;
  }
  return qs.stringify(
    {
      sort: ["publishedAt:desc"],
      filters: {
        $and: [
          { slug: { $ne: slugParam.value } },
          {
            category: {
              slug: { $eq: productCategorySlug.value },
            },
          },
        ],
      },
      fields: ["title", "slug", "documentId"],
      populate: {
        productOverview: {
          fields: [
            "sku",
            "price",
            "oldPrice",
            "currency",
            "color",
            "mediaSize",
            "objectFit",
          ],
          populate: { media: true },
        },
      },
      pagination: {
        page: 1,
        pageSize: 9,
      },
    },
    { encodeValuesOnly: true },
  );
});

const relatedProductsEndpoint = computed(() => {
  if (!relatedProductsQuery.value) {
    return null;
  }
  return `product-lists?${relatedProductsQuery.value}`;
});

/* ---------- Computed Sınıflar ---------- */
const productGridClass = computed(() => [
  "grid grid-cols-1 lg:grid-cols-2 overflow-hidden bg-white border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]",
  "rounded-2xl md:rounded-3xl",
]);

const mediaContainerClass = computed(() => {
  if (hasMultipleMedia.value) {
    return ["w-full h-full flex flex-col gap-4"];
  }
  return ["w-full h-full flex flex-col gap-4 lg:flex-row lg:items-start"];
});

const mainImageLinkClass = computed(() => [{ "rounded-lg": siteSettingsStore.roundCorner }]);

const thumbButtonClass = computed(() => [
  "thumb-btn cursor-pointer w-full block relative overflow-hidden bg-white focus:outline-none focus-visible:ring focus-visible:ring-brand-first",
  { "rounded-xl": siteSettingsStore.roundCorner, "rounded-lg": !siteSettingsStore.roundCorner },
]);

// Related products: fetched directly with $fetch instead of going through
// useApi/useAsyncData. The shared asyncData key would otherwise collide
// across ProductDetail remounts (the endpoint starts as null, so every
// instance registers under the same "api-null-..." key) and the cached
// state from a previous product can leak into the next one or get cleared
// when the previous instance unmounts, hiding the section.
const relatedProductsStateKey = computed(
  () => `related-products-${slugParam.value}-${locale.value}`,
);
const relatedProducts = useState(relatedProductsStateKey.value, () => []);
const flattenStrapiData = (data) => {
  if (!data || typeof data !== "object") return data;
  if (Array.isArray(data)) return data.map(flattenStrapiData);
  if (data.id !== undefined && data.attributes !== undefined) {
    return { id: data.id, ...flattenStrapiData(data.attributes) };
  }
  if (data.data !== undefined) {
    const keys = Object.keys(data);
    if (keys.length === 1 || (keys.length === 2 && keys.includes("meta"))) {
      return flattenStrapiData(data.data);
    }
  }
  const out = {};
  for (const k in data) out[k] = flattenStrapiData(data[k]);
  return out;
};
const loadRelatedProducts = async (endpoint) => {
  if (!endpoint) {
    relatedProducts.value = [];
    return;
  }
  try {
    const result = await $fetch("/api/strapi-get", {
      params: { endpoint, locale: locale.value },
    });
    const items = Array.isArray(result?.data)
      ? result.data.map(flattenStrapiData)
      : [];
    relatedProducts.value = items;
  } catch (_err) {
    relatedProducts.value = [];
  }
};
const relatedProductsMini = computed(() => relatedProducts.value.slice(0, 11));

const buildProductLink = (item) => {
  const slug = item?.slug;
  if (!slug) {
    return baseProductsPath.value;
  }
  const prefix = locale.value === "tr" ? "" : `/${locale.value}`;
  return `${prefix}/${encodeURIComponent(slug)}`;
};

/* -------------------- 🔧 SEO Etiketi -------------------- */
const seoData = computed(() => product.value?.seo ?? {});
const pageTitle = computed(() => productTitle.value);
const pageDescription = computed(
  () => heroDescription.value || productDescription.value || "",
);

useMetaTags(seoData, pageTitle, pageDescription);

useHead(() => {
  const item = product.value;
  if (!item) {
    return { script: [] };
  }

  const faqSection = item.sections?.find(
    (section) =>
      ["sections.accordion", "sections.image-accordion"].includes(
        section.__component,
      ) && section.enabled,
  );

  const currency = productOverview.value?.currency || "TRY";
  const priceNumber =
    typeof productOverview.value?.price === "number"
      ? productOverview.value.price
      : null;
  const availability =
    item?.stockStatus === "in_stock" || item?.available
      ? "https://schema.org/InStock"
      : priceNumber !== null
        ? "https://schema.org/PreOrder"
        : "https://schema.org/OutOfStock";
  const imageMedia = productOverview.value?.media?.[0] || item.listMedia?.[0];
  const brandName =
    item.brand?.title?.trim() || siteSettingsStore.brandName || undefined;

  const productSchema = createWebPageSchema({
    type: "Product",
    url: fullUrl.value,
    name: item.title || heroTitle.value,
    headline: heroTitle.value || item.title,
    description:
      item.seo?.description ||
      heroDescription.value ||
      productDescription.value ||
      stripHtmlTags(item.description),
    publishedAt: item.publishedAt,
    updatedAt: item.updatedAt,
    keywords: item.seo?.keywords,
    baseUrl,
    mainEntityType: "Product",
    image: imageMedia?.url
      ? {
          url: imageMedia.url,
          width: imageMedia.width,
          height: imageMedia.height,
        }
      : undefined,
  });

  if (productSchema) {
    if (productSku.value) {
      productSchema.sku = productSku.value;
      productSchema.productID = productSku.value;
    }

    if (brandName) {
      productSchema.brand = {
        "@type": "Brand",
        name: brandName,
      };
    }

    if (priceNumber !== null) {
      productSchema.offers = {
        "@type": "Offer",
        priceCurrency: currency,
        price: priceNumber,
        priceValidUntil: item.offerValidUntil || undefined,
        availability,
        url: fullUrl.value,
        sku: productSku.value || undefined,
        itemCondition: "https://schema.org/NewCondition",
      };

      if (productOverview.value?.oldPrice) {
        productSchema.offers.priceSpecification = {
          "@type": "UnitPriceSpecification",
          priceCurrency: currency,
          price: productOverview.value?.oldPrice,
          referenceQuantity: 1,
        };
      }
    }
  }

  const scripts = buildJsonLdScripts([
    {
      key: "schema-product-detail",
      schema: productSchema,
    },
    {
      key: "schema-product-detail-faq",
      schema: createFaqSchema(faqSection?.items),
    },
    {
      key: "schema-product-detail-breadcrumbs",
      schema: createBreadcrumbSchema(
        breadcrumbs.value.map((b) => ({
          title: b.title.replace(/<[^>]*>?/gm, "").trim() || t("Home"),
          link: b.link,
        })),
        siteUrl,
      ),
    },
  ]);

  return {
    script: scripts,
  };
});

const mainSwiperModules = [Navigation, Thumbs];
const thumbsSwiperModules = [FreeMode, Thumbs];
const maxThumbs = 6;
const thumbSlidesPerView = computed(() => {
  return isMobile.value ? 5 : maxThumbs;
});
const thumbsDirection = computed(() => "horizontal");

await fetchProductData();

// Explicitly trigger 404 if no product is found
if (import.meta.server && (!product.value || !product.value.title)) {
  triggerError(404, "Ürün bulunamadı");
}

// Trigger the related-products load each time the endpoint resolves.
// On the server we await it so SSR includes the section; on the client
// the watcher keeps it in sync as the endpoint changes.
if (import.meta.server) {
  await loadRelatedProducts(relatedProductsEndpoint.value);
} else {
  watch(
    relatedProductsEndpoint,
    (endpoint) => {
      loadRelatedProducts(endpoint);
    },
    { immediate: true },
  );
}
const productImageClass = computed(() => {
  const size = productOverview.value?.mediaSize;
  const aspect = {
    'aspect-square': size === 'aspect-ratio-1/1',
    'aspect-[3/2]': size === 'aspect-ratio-3/2',
    'aspect-[3/4]': size === 'aspect-ratio-3/4',
    'aspect-[4/3]': size === 'aspect-ratio-4/3',
    'aspect-[4/5]': size === 'aspect-ratio-4/5',
    'aspect-video': !size || size === 'aspect-ratio-16/9'
  };
  const fit = {
    'object-contain': productOverview.value?.objectFit === 'contain',
    'object-cover object-center': !productOverview.value?.objectFit || productOverview.value?.objectFit === 'cover'
  };
  return [aspect, fit];
});

const shouldShowColorSwatch = computed(() => !hasSingleMedia.value && !hasMultipleMedia.value && !!productOverviewColor.value);

const productColorSwatchClass = computed(() => {
  const size = productOverview.value?.mediaSize;
  return {
    'aspect-square': size === 'aspect-ratio-1/1',
    'aspect-[3/2]': size === 'aspect-ratio-3/2',
    'aspect-[3/4]': size === 'aspect-ratio-3/4',
    'aspect-[4/3]': size === 'aspect-ratio-4/3',
    'aspect-[4/5]': size === 'aspect-ratio-4/5',
    'aspect-video': !size || size === 'aspect-ratio-16/9'
  };
});

const productThumbImageClass = computed(() => {
  const size = productOverview.value?.mediaSize;
  const aspect = {
    'aspect-square': size === 'aspect-ratio-1/1',
    'aspect-[3/2]': size === 'aspect-ratio-3/2',
    'aspect-[3/4]': size === 'aspect-ratio-3/4',
    'aspect-[4/3]': size === 'aspect-ratio-4/3',
    'aspect-[4/5]': size === 'aspect-ratio-4/5',
    'aspect-video': !size || size === 'aspect-ratio-16/9'
  };
  const fit = {
    'object-contain': productOverview.value?.objectFit === 'contain',
    'object-cover object-center': !productOverview.value?.objectFit || productOverview.value?.objectFit === 'cover'
  };
  return [aspect, fit];
});

const relatedProductsBreakpoints = {
  0: { slidesPerView: 3.7 },
  480: { slidesPerView: 3.5 },
  640: { slidesPerView: 4.5 },
  1024: { slidesPerView: 5.4 },
};
const fancyboxOptions = {
  Carousel: {
    infinite: false,
  },
};
</script>
<style lang="scss">
.swiper-product-thumb {
  .swiper-slide {
    button {
      @apply border border-solid border-gray-100 transition-all duration-200 box-border;
      &:hover {
        @apply border-gray-300;
      }
    }
  }
  .swiper-slide-thumb-active {
    button {
      @apply border-gray-300;
    }
  }
}
.swiper-related-products {
  @apply relative overflow-visible;
  touch-action: pan-y;

  .swiper-wrapper {
    @apply items-stretch;
  }

  .swiper-slide {
    @apply h-auto;
    user-select: none;
    -webkit-user-drag: none;

    a {
      @apply h-full;
      -webkit-user-drag: none;
    }

    img {
      pointer-events: none;
      -webkit-user-drag: none;
    }
  }
}
</style>
